import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertCircle, ArrowLeft, Bot, Check, CheckCheck, Clock, FileText, Loader2, MessageCircle, Paperclip, Search, Send, UserCircle, X } from "lucide-react";
import { QuickReplies } from "@/components/messages/QuickReplies";
import { WaProfileModal } from "@/components/messages/WaProfileModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEND_TYPES = { "image/jpeg": 5, "image/png": 5, "application/pdf": 16 };
const errText = (e) => { const d = e.response?.data?.detail; return Array.isArray(d) ? d.map((x) => x.msg).join(" ") : d || "Mesaj gönderilemedi"; };
export const fmtPhone = (wa) => (wa.length === 12 && wa.startsWith("90") ? `+90 ${wa.slice(2, 5)} ${wa.slice(5, 8)} ${wa.slice(8, 10)} ${wa.slice(10)}` : `+${wa}`);
const initials = (name) => (name || "").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toLocaleUpperCase("tr-TR")).join("");
const clock = (iso) => new Date(iso).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
const dayLabel = (iso) => { const d = new Date(iso), t = new Date(); const y = new Date(t); y.setDate(t.getDate() - 1); return d.toDateString() === t.toDateString() ? "Bugün" : d.toDateString() === y.toDateString() ? "Dün" : d.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" }); };
const shortTime = (iso) => (new Date(iso).toDateString() === new Date().toDateString() ? clock(iso) : new Date(iso).toLocaleDateString("tr-TR", { day: "numeric", month: "short" }));
const timeLeft = (iso) => { const ms = iso ? new Date(iso) - Date.now() : 0; if (ms <= 0) return null; const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000); return h ? `${h} sa ${m} dk` : `${m} dk`; };
const preview = (m) => m.template ? `Otomatik · ${m.template}` : m.body ? m.body.split("\n")[0] : m.media.some((f) => f.type?.startsWith("image/")) ? "Fotoğraf" : m.media.length || m.media_pending ? "Dosya" : "";

export function useUnreadMessages(enabled, onOpen) {
  const [count, setCount] = useState(0);
  const seen = useRef(undefined), open = useRef(onOpen);
  open.current = onOpen;
  const check = useCallback(() => axios.get(`${API}/messages/unread`).then(({ data }) => {
    setCount(data.count);
    const id = data.latest?.id || null;
    const multi = (data.lines || []).length > 1;
    if (seen.current !== undefined && id && id !== seen.current) toast(`${data.latest.name} yeni mesaj gönderdi`, { description: multi && data.latest.line_label ? `${data.latest.line_label}: ${data.latest.body}` : data.latest.body, action: { label: "Aç", onClick: () => open.current() } });
    seen.current = id;
  }).catch(() => {}), []);
  useEffect(() => { if (!enabled) return; check(); const t = setInterval(check, 20000); return () => clearInterval(t); }, [enabled, check]);
  return [count, check];
}

function Avatar({ name }) {
  return <span className="inbox-avatar">{initials(name) || <MessageCircle size={15} />}</span>;
}

function ConversationList({ list, active, onPick }) {
  const [q, setQ] = useState("");
  const needle = q.trim().toLocaleLowerCase("tr-TR"), num = needle.replace(/\D/g, "");
  const shown = (list || []).filter((c) => !needle || (c.name || "").toLocaleLowerCase("tr-TR").includes(needle) || (num && c.wa.includes(num)));
  return (
    <aside className="inbox-list" data-testid="conversation-list">
      <label className="inbox-search"><Search size={15} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="İsim veya numara ara" data-testid="inbox-search-input" /></label>
      {list === null && <p className="empty" data-testid="conversations-loading">Konuşmalar yükleniyor…</p>}
      {list && shown.length === 0 && <p className="empty" data-testid="conversations-empty">{list.length ? "Aramanızla eşleşen konuşma yok." : "Henüz mesaj yok. Müşterileriniz WhatsApp'tan yazdığında burada görünür."}</p>}
      {shown.map((c) => (
        <button key={c.wa} className={active === c.wa ? "inbox-item active" : "inbox-item"} onClick={() => onPick(c.wa)} data-testid={`conversation-${c.wa}`}>
          <Avatar name={c.name} />
          <span className="inbox-meta"><b>{c.name || fmtPhone(c.wa)}</b><small className={c.unread ? "strong" : ""}>{["failed", "undelivered"].includes(c.last.status) && c.last.direction === "out" && <AlertCircle size={12} className="tick failed" />}{c.last.direction === "out" && !c.last.template ? "Siz: " : ""}{preview(c.last)}</small></span>
          <span className="inbox-side"><time>{shortTime(c.last.created_at)}</time>{c.unread > 0 && <em data-testid={`conversation-unread-${c.wa}`}>{c.unread}</em>}</span>
        </button>
      ))}
    </aside>
  );
}

function StatusIcon({ status }) {
  if (["failed", "undelivered"].includes(status)) return <AlertCircle size={13} className="tick failed" />;
  if (status === "read") return <CheckCheck size={14} className="tick read" />;
  if (status === "delivered") return <CheckCheck size={14} className="tick" />;
  return <Check size={13} className="tick" />;
}

function useBlobUrl(src, enabled) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!enabled) return;
    let u, alive = true;
    axios.get(src, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); if (alive) setUrl(u); }).catch(() => {});
    return () => { alive = false; if (u) URL.revokeObjectURL(u); };
  }, [src, enabled]);
  return url;
}

function Media({ sid, i, file }) {
  const src = `${API}/messages/media/${sid}/${i}`, kind = file.type?.split("/")[0];
  const url = useBlobUrl(src, kind === "image" || kind === "audio" || kind === "video");
  const download = async () => {
    try {
      const r = await axios.get(src, { responseType: "blob" });
      const u = URL.createObjectURL(r.data), a = Object.assign(document.createElement("a"), { href: u, download: file.name });
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(u), 10000);
    } catch { toast.error("Dosya açılamadı"); }
  };
  if (kind === "image") return url ? <img className="bubble-img" src={url} alt="Fotoğraf" onClick={() => window.open(url, "_blank")} data-testid={`message-image-${sid}-${i}`} /> : <span className="bubble-img loading" />;
  if (kind === "audio") return url ? <audio controls src={url} data-testid={`message-audio-${sid}-${i}`} /> : <span className="bubble-file"><Loader2 size={14} className="spin" />Ses kaydı</span>;
  if (kind === "video") return url ? <video controls className="bubble-img" src={url} data-testid={`message-video-${sid}-${i}`} /> : <span className="bubble-img loading" />;
  return <button type="button" className="bubble-file" onClick={download} data-testid={`message-file-${sid}-${i}`}><FileText size={16} /><span>{file.name}</span></button>;
}

function Bubble({ m }) {
  return (
    <div className={`bubble ${m.direction}${m.template ? " auto" : ""}`} data-testid={`message-${m.id}`}>
      {m.template && <span className="bubble-tag"><Bot size={12} />Otomatik · {m.template}</span>}
      {m.media.map((f, i) => <Media key={i} sid={m.id} i={i} file={f} />)}
      {m.media_pending && <span className="bubble-file"><Loader2 size={14} className="spin" />Dosya alınıyor…</span>}
      {m.body && <p>{m.body}</p>}
      <span className="bubble-meta">{clock(m.created_at)}{m.direction === "out" && <StatusIcon status={m.status} />}</span>
      {m.error && <span className="bubble-error" data-testid={`message-error-${m.id}`}><AlertCircle size={12} />{m.error}</span>}
    </div>
  );
}

function Composer({ wa, line, ends, name, onSent }) {
  const [text, setText] = useState(""), [file, setFile] = useState(null), [busy, setBusy] = useState(false);
  const input = useRef(null);
  const pickReply = (t) => { setText((cur) => (cur.trim() ? `${cur.trimEnd()} ${t}` : t)); setTimeout(() => input.current?.focus(), 0); };
  if (!timeLeft(ends)) return <div className="composer closed" data-testid="composer-closed"><Clock size={16} /><span>Bu kişi son 24 saatte size yazmadığı için WhatsApp kuralı gereği şu an cevap gönderilemez. Kişi size tekrar yazdığında buradan cevap verebilirsiniz.</span></div>;
  const pick = (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const max = SEND_TYPES[f.type];
    if (!max) return toast.error("Sadece JPG, PNG veya PDF gönderebilirsiniz");
    if (f.size > max * 1024 * 1024) return toast.error(`Bu dosya en fazla ${max} MB olabilir`);
    setFile(f);
  };
  const send = async (e) => {
    e.preventDefault();
    if (busy || (!text.trim() && !file)) return;
    setBusy(true);
    try {
      let r;
      if (file) { const fd = new FormData(); fd.append("file", file); fd.append("body", text.trim()); r = await axios.post(`${API}/messages/${wa}/media?line=${line}`, fd); }
      else r = await axios.post(`${API}/messages/${wa}/reply?line=${line}`, { body: text.trim() });
      onSent(r.data); setText(""); setFile(null);
    } catch (err) { toast.error(errText(err)); }
    setBusy(false);
  };
  return (
    <form className="composer" onSubmit={send} data-testid="composer">
      <QuickReplies name={name} onPick={pickReply} />
      {file && <div className="composer-file" data-testid="composer-file"><FileText size={15} /><span>{file.name}</span><button type="button" onClick={() => setFile(null)} title="Kaldır" data-testid="composer-file-remove"><X size={13} /></button></div>}
      <div className="composer-row">
        <label className="icon-btn" title="Fotoğraf veya PDF ekle" data-testid="composer-attach-button"><Paperclip size={18} /><input type="file" hidden accept="image/jpeg,image/png,application/pdf" onChange={pick} data-testid="composer-file-input" /></label>
        <textarea ref={input} rows={1} value={text} maxLength={file ? 1000 : 4000} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(e); }} placeholder={file ? "Açıklama ekleyin (isteğe bağlı)" : "Mesajınızı yazın…"} data-testid="composer-input" />
        <button className="primary-btn" disabled={busy || (!text.trim() && !file)} data-testid="composer-send-button">{busy ? <Loader2 size={15} className="spin" /> : <Send size={15} />}Gönder</button>
      </div>
    </form>
  );
}

function Thread({ wa, line, onBack, onChanged }) {
  const [data, setData] = useState(null);
  const end = useRef(null), lastId = useRef(null);
  const load = useCallback(() => axios.get(`${API}/messages/${wa}?line=${line}`).then((r) => setData(r.data)).catch(() => {}), [wa, line]);
  useEffect(() => { setData(null); lastId.current = null; load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    const msgs = data?.messages || [], last = msgs[msgs.length - 1];
    if (last && last.id !== lastId.current) { lastId.current = last.id; end.current?.scrollIntoView({ block: "end" }); }
    if (msgs.some((m) => m.unread)) axios.post(`${API}/messages/${wa}/read?line=${line}`).then(onChanged).catch(() => {});
  }, [data, wa, line, onChanged]);
  const left = timeLeft(data?.window_ends);
  return (
    <div className="thread" data-testid="thread">
      <header className="thread-head">
        <button className="icon-btn thread-back" onClick={onBack} title="Konuşmalar" data-testid="thread-back-button"><ArrowLeft size={18} /></button>
        <Avatar name={data?.name} />
        <div><b data-testid="thread-name">{data?.name || fmtPhone(wa)}</b><small>{fmtPhone(wa)}</small></div>
        {data && <span className={left ? "window-pill open" : "window-pill"} data-testid="reply-window-status"><Clock size={13} />{left ? `Cevap süresi: ${left}` : "24 saat doldu"}</span>}
      </header>
      <div className="thread-body" data-testid="thread-messages">
        {!data && <p className="empty">Mesajlar yükleniyor…</p>}
        {data?.messages.map((m, i) => (
          <div key={m.id} className="bubble-wrap">
            {(i === 0 || dayLabel(data.messages[i - 1].created_at) !== dayLabel(m.created_at)) && <span className="day-chip">{dayLabel(m.created_at)}</span>}
            <Bubble m={m} />
          </div>
        ))}
        <div ref={end} />
      </div>
      {data && <Composer wa={wa} line={line} ends={data.window_ends} name={data.name} onSent={(m) => { setData((d) => ({ ...d, messages: [...d.messages, m] })); onChanged(); }} />}
    </div>
  );
}

function LineTabs({ lines, line, onPick }) {
  if (lines.length < 2) return null;
  return (
    <div className="line-tabs" data-testid="line-tabs">
      {lines.map((l) => (
        <button key={l.id} className={l.id === line ? "line-tab active" : "line-tab"} onClick={() => onPick(l.id)} data-testid={`line-tab-${l.id}`}>
          <MessageCircle size={15} /><span><b>{l.label}</b><small>{fmtPhone(l.id)}</small></span>{l.unread > 0 && <em data-testid={`line-tab-unread-${l.id}`}>{l.unread}</em>}
        </button>
      ))}
    </div>
  );
}

export default function Messages({ onUnread }) {
  const [list, setList] = useState(null), [lines, setLines] = useState([]), [line, setLine] = useState(null), [active, setActive] = useState(null), [profile, setProfile] = useState(false);
  const cur = useRef(line);
  cur.current = line;
  const loadList = useCallback(() => axios.get(`${API}/messages`, { params: line ? { line } : {} }).then((r) => {
    if (cur.current && r.data.line !== cur.current) return;
    setList(r.data.conversations); setLines(r.data.lines); if (!line && r.data.line) setLine(r.data.line);
  }).catch(() => setList((l) => l || [])), [line]);
  useEffect(() => { loadList(); const t = setInterval(loadList, 10000); return () => clearInterval(t); }, [loadList]);
  const changed = useCallback(() => { loadList(); onUnread?.(); }, [loadList, onUnread]);
  const pickLine = (id) => { if (id === line) return; setActive(null); setList(null); setLine(id); };
  return (
    <>
      <div className="page-title"><div><span className="eyebrow">WHATSAPP</span><h1 data-testid="page-title">Mesajlar</h1><p>Müşterilerinizden gelen WhatsApp mesajlarını görün ve 24 saat içinde cevap verin.</p></div>
        <button className="outline-btn" onClick={() => setProfile(true)} data-testid="wa-profile-button"><UserCircle size={16} />WhatsApp profili</button></div>
      {profile && <WaProfileModal onClose={() => setProfile(false)} />}
      <LineTabs lines={lines} line={line} onPick={pickLine} />
      <section className={active ? "panel inbox thread-open" : "panel inbox"} data-testid="inbox">
        <ConversationList list={list} active={active} onPick={setActive} />
        {active && line ? <Thread key={`${line}-${active}`} wa={active} line={line} onBack={() => setActive(null)} onChanged={changed} /> : (
          <div className="inbox-placeholder" data-testid="inbox-placeholder"><MessageCircle size={30} /><b>Bir konuşma seçin</b><span>Soldaki listeden bir kişiye tıklayarak mesajları görün ve cevap yazın.</span></div>
        )}
      </section>
    </>
  );
}
